/**
 * @file vehicleHelpers.js
 * @description Funciones de utilidad para el manejo y visualización de vehículos
 */

// Helper para parsear fechas flexibles (ISO o DD/MM/YYYY)
const parseFlexibleDate = (dateString) => {
    if (!dateString) return null;

    // Si ya es un objeto fecha valido
    if (dateString instanceof Date) return dateString;

    // Intento 1: Formato DD/MM/YYYY o DD/MM/YYYY HH:mm:ss (Prioridad para evitar ambigüedad MM/DD vs DD/MM)
    const ddmmyyyyPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/;
    const match = String(dateString).match(ddmmyyyyPattern);

    if (match) {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1; // Meses en JS son 0-11
        const year = parseInt(match[3], 10);

        // Si tiene parte de tiempo
        const timePart = String(dateString).split(' ')[1];
        let date;

        if (timePart) {
            const [hours, minutes, seconds] = timePart.split(':').map(Number);
            date = new Date(year, month, day, hours || 0, minutes || 0, seconds || 0);
        } else {
            date = new Date(year, month, day);
        }

        if (!isNaN(date.getTime())) return date;
    }

    // Intento 2: Parseo estándar (ISO, etc) - Solo si falla el formato explícito
    let date = new Date(dateString);
    if (!isNaN(date.getTime())) return date;

    return null;
};

/**
 * Formatea fecha en formato DD/MM/YYYY
 */
export const formatDate = (dateString) => {
    const date = parseFlexibleDate(dateString);
    if (!date) return '-';

    return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

/**
 * Formatea fecha y hora en formato DD/MM/YYYY, HH:MM
 */
export const formatDateTime = (dateString) => {
    const date = parseFlexibleDate(dateString);
    if (!date) return '-';

    return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }) + ', ' + date.toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
};

/**
 * Obtiene código hexadecimal para un nombre de color
 * Usado para mostrar círculos de color en la tabla
 */
export const getColorCode = (colorName) => {
    const colorMap = {
        'Negro': '#000000',
        'Blanco': '#FFFFFF',
        'Rojo': '#EF4444',
        'Azul': '#3B82F6',
        'Verde': '#10B981',
        'Gris': '#6B7280',
        'Plata': '#C0C0C0',
        'Dorado': '#D4AF37',
        'Café': '#8B4513',
        'Beige': '#F5F5DC'
    };

    const colorKey = Object.keys(colorMap).find(key =>
        colorName && colorName.toLowerCase().includes(key.toLowerCase())
    );

    return colorMap[colorKey] || '#9CA3AF';
};

/**
 * Obtiene clases CSS para badges de estatus
 */
export const getStatusBadgeColor = (estatusId) => {
    const colorMap = {
        1: 'bg-blue-100 text-blue-800',
        3: 'bg-blue-100 text-blue-800',
        6: 'bg-pink-100 text-pink-800',
        7: 'bg-red-800 text-white'
    };
    return colorMap[estatusId] || 'bg-gray-100 text-gray-800';
};

/**
 * FUNCIÓN CRÍTICA: Determina si un vehículo está en sitio
 * Debe coincidir exactamente con vehiculoEstaEnMovimiento de patiosData.js
 *
 * Está FUERA de sitio solo si su última lectura de antena fue de salida
 * (modo_antena 'salida' o 'salida_definitiva'). El número de antena no importa.
 * Sin lectura, o con una lectura sin modo_antena, no se marca como fuera.
 *
 * @param {Object} vehiculo - Vehículo del comparativo (con lectura_rfid)
 * @returns {boolean} true si está en sitio
 */
export const isEnSitio = (vehiculo) => {
    const { tipo_lectura, modo_antena } = vehiculo?.lectura_rfid || {};
    if (tipo_lectura === 'pistola_manual') return true;
    return modo_antena !== 'salida' && modo_antena !== 'salida_definitiva';
};

/**
 * Obtiene color corporativo de una marca
 */
export const getMarcaColor = (marca) => {
    const colorMap = {
        'FORD': '#1D4ED8',
        'LINCOLN': '#059669',
        'GWM': '#D61117',
        'MAZDA': '#1E8ECC'
    };
    return colorMap[marca] || '#6B7280';
};

/**
 * Determina si un vehículo es "facturado sin fecha de recibo" — es decir,
 * ya tiene número de factura pero aún no llega físicamente al lote.
 * Estos vehículos se consideran "Compradas por llegar", no "faltantes RFID".
 *
 * @param {Object} vehiculo - Objeto vehículo del comparativo
 * @returns {boolean} true si está facturado pero sin fecha de recibo
 */
export const esFacuradoSinFechaRecibo = (vehiculo) => {
    const tieneFactura = !!(vehiculo.VTE_DOCTO || vehiculo.vte_docto || vehiculo.NumeroFactura || vehiculo.numero_factura);
    const tieneFechaRecibo = !!(vehiculo.FechaRecibo || vehiculo.fecharecibo);
    return tieneFactura && !tieneFechaRecibo;
};

/**
 * Determina si un vehículo debe contar para la detección RFID (lecturaRFID/
 * faltantesRFID). Se excluyen los En Tránsito (estatus 3, aún no llegan
 * físicamente) y los facturados sin fecha de recibo (compra en camino).
 * Traspaso Sin Recibir (estatus 5) SÍ cuenta, como inventario normal.
 *
 * @param {Object} vehiculo - Objeto vehículo del comparativo
 * @returns {boolean} true si debe evaluarse para lecturaRFID/faltantesRFID
 */
export const debeContarParaRFID = (vehiculo) => {
    return vehiculo.estatus !== 3 && !esFacuradoSinFechaRecibo(vehiculo);
};


